import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { ComputedQuery, FiltersMap, PickerState } from '@wix/bex-core';
import { Box, BoxProps, Loader } from '@wix/design-system';
import { PickerEmptyState } from '../PickerEmptyState';

export interface PickerProps<T, F extends FiltersMap> {
  dataHook?: BoxProps['dataHook'];
  flex?: CSSProperties['flex'];
  state: PickerState<T, F>;
  renderError: (params: { err: unknown; isOnline: boolean }) => ReactElement;
  renderEmptyState?: (params: {
    hasAvailableItems: boolean;
    query: ComputedQuery<F>;
  }) => ReactElement;
  hasFilterDropdown?: boolean;
  children: ReactNode;
}

function _Picker<T, F extends FiltersMap = FiltersMap>(
  props: PickerProps<T, F>,
) {
  const {
    state,
    renderEmptyState,
    renderError,
    dataHook,
    children,
    flex,
    hasFilterDropdown,
  } = props;
  const { collection, minContentHeight } = state;
  const {
    showRefreshLoader,
    result: { status, isEmptyAndNotFetching },
  } = collection;

  let child: ReactNode;
  if (status.isIdle || showRefreshLoader) {
    // the loader container should be according to the last number of items height (`contentHeight`)
    child = (
      <Box
        height="100%"
        align="center"
        verticalAlign="middle"
        minHeight={minContentHeight}
      >
        <Loader dataHook="search-loader" />
      </Box>
    );

    child = (
      <Box direction="vertical" overflow="auto">
        {child}
      </Box>
    );
  } else if (status.isError) {
    child = renderError({
      err: status.error,
      isOnline: collection.isOnline(),
    });
    child = (
      <Box
        align="center"
        verticalAlign="middle"
        paddingTop="SP2"
        paddingBottom="SP9"
        height="100%"
        minHeight={minContentHeight}
      >
        {child}
      </Box>
    );

    child = (
      <Box direction="vertical" overflow="auto">
        {child}
      </Box>
    );
  } else if (isEmptyAndNotFetching) {
    child = (
      <PickerEmptyState
        state={state}
        hasFilterDropdown={hasFilterDropdown}
        renderEmptyState={renderEmptyState}
      />
    );
  } else {
    child = (
      <Box minHeight={minContentHeight} direction="vertical">
        {children}
      </Box>
    );
  }

  return (
    <Box dataHook={dataHook} direction="vertical" overflow="hidden" flex={flex}>
      {child}
    </Box>
  );
}

export const Picker = observer(_Picker);
